import { errorEnvelope } from '../shared/utils/mockServiceHelpers';

/**
 * Manual QA switches for the mock layer (development builds only, like `?mockActor`). Open any
 * People & Access URL with:
 *
 *   ?mockDelay=2500        the member services answer after 2.5 s (to see the loading states)
 *   ?mockFail=stats,list   those requests fail (to see the error states); `stats` is the header
 *                          indicators, `list` the members table, `invite` the Send invitation action,
 *                          `studios` saving, granting or removing a member's studio access,
 *                          `roles`, `groups`, `resources` and `security` the changes of the member
 *                          detail tabs (assign or remove a role or group, remove resource access,
 *                          the MFA reminder),
 *                          `remove` the Remove from workspace, Reactivate and Delete user actions
 *   ?mockImport=1          the list lands as right after a bulk import: two new people in an import
 *                          batch, the batch filter applied and their rows marked New
 *   ?mockQa=reset          clears all of them
 *
 * The choice is kept for the browser session, because the shell's workspace redirect strips the
 * query string. Production builds ignore all of it.
 */
const STORAGE_KEY = 'people-access:mock-qa';

const readFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      reset: params.get('mockQa') === 'reset',
      delay: params.get('mockDelay'),
      fail: params.get('mockFail'),
      import: params.get('mockImport'),
    };
  } catch {
    return { reset: false, delay: null, fail: null, import: null };
  }
};

const readStored = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)) ?? {};
  } catch {
    return {};
  }
};

const writeStored = (value) => {
  try {
    if (value) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage can be blocked (private windows); the URL param still works for this load.
  }
};

/** The active switches: the URL wins and is remembered; `?mockQa=reset` forgets them. */
const resolveSwitches = () => {
  const { reset, delay, fail, import: importFlag } = readFromUrl();
  if (reset) {
    writeStored(null);
    return { delay: 0, fail: [], import: false };
  }
  if (delay !== null || fail !== null || importFlag !== null) {
    const next = { ...readStored() };
    if (importFlag !== null) next.import = importFlag === '1';
    if (delay !== null) next.delay = Number(delay) || 0;
    if (fail !== null)
      next.fail = fail
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    writeStored(next);
  }
  const stored = readStored();
  return {
    delay: Number(stored.delay) || 0,
    fail: Array.isArray(stored.fail) ? stored.fail : [],
    import: Boolean(stored.import),
  };
};

/** `?mockImport=1` is on (development builds only). */
export const isMockImportActive = () => import.meta.env.DEV && resolveSwitches().import;

/**
 * Runs a mock service call through the QA switches. `name` is the switch it answers to
 * (`stats`, `list`, `invite`, `studios`, `remove`); `produce` returns the normal envelope promise.
 */
export const withMockQa = (name, produce) => {
  if (!import.meta.env.DEV) return produce();

  const { delay, fail } = resolveSwitches();
  if (!delay && !fail.includes(name)) return produce();

  return new Promise((resolve) => {
    setTimeout(
      () =>
        resolve(
          fail.includes(name)
            ? errorEnvelope(500, 'MOCK_QA_FAILURE', `Forced failure of "${name}" (?mockFail).`)
            : produce()
        ),
      delay
    );
  });
};
